import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison Levallois-Perret + 2 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos fournitures de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne gaspillez pas votre temps dans les embouteillages ou à charger
            des liasses de cartons poussiéreuses dans un véhicule inadapté.
            Nos équipes prennent en main l&apos;intégralité de la chaîne
            logistique pour vous livrer au cœur de {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Filière interne exclusive",
              body: "Nous excluons totalement les transporteurs tiers et les plateformes de messagerie. C'est notre propre structure francilienne qui charge et dépose vos fournitures, garantissant une manipulation soignée et un respect strict de vos horaires.",
              Icon: Truck,
            },
            {
              title: "Contrôle d'intégrité",
              body: "Voyageant à l'abri des intempéries dans nos fourgons hermétiques, vos modules de carton conservent leur taux d'hygrométrie idéal. Vous réceptionnez un matériel parfaitement rigide, sain et prêt à l'emploi.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Blindage antichoc pour objets fragiles ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection des objets fragiles
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Blindage antichoc : protégez vos objets fragiles avec nos
            solutions d&apos;emballage
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la vaisselle, les écrans et les objets d&apos;art paient
            souvent le prix fort lors des transports urbains, nous avons
            développé des barrières de protection spécifiques pour neutraliser
            le moindre risque.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Les matrices de compartimentage",
              body: "Nos valises et boîtes techniques pour verres et assiettes intègrent des séparateurs rigides. Chaque pièce est isolée dans sa propre cellule, évitant les frictions et les chocs directs entre les objets.",
              Icon: Boxes,
            },
            {
              title: "Les complexes bulles haute densité",
              body: "Nos films protecteurs ne se dégonflent pas sous la pression. Ils créent un coussin d'air permanent autour de vos équipements électroniques et de vos bibelots pour absorber l'énergie des vibrations de la route.",
              Icon: Layers,
            },
            {
              title: "Les matelas de calage",
              body: "Des intercalaires et des protections d'angles conçus pour combler les vides à l'intérieur de vos colis et bloquer le mobilier, garantissant que rien ne bouge pendant les virages ou les freinages.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-levallois-cta-emballage"
          >
            Voir nos protections
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-levallois-cta-fermeture"
          >
            <Scissors className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs et accessoires
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi choisir nos cartons (3 avantages) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir nos cartons et nous confier vos préparatifs&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire le choix de nos fournitures, c&apos;est opter pour une
            rigueur professionnelle pensée pour éliminer le stress de
            l&apos;emballage grâce à des critères stricts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Un standard de solidité supérieur",
              body: "Nos cartons affichent une résistance au gerbage (l'empilement vertical) bien supérieure aux produits du commerce, interdisant tout affaissement dans le camion.",
              Icon: ShieldCheck,
            },
            {
              title: "Une réactivité locale totale",
              body: "Grâce à notre stock permanent en Île-de-France, nous sommes capables de répondre à vos besoins urgents et d'ajuster vos dotations en un temps record pour ne jamais bloquer votre planning.",
              Icon: Truck,
            },
            {
              title: "Une transparence tarifaire",
              body: "Pas de fausses promotions ni de coûts masqués. Vous achetez du matériel de calibre industriel au prix le plus juste du marché, directement auprès d'un expert de la logistique.",
              Icon: Tag,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-levallois-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-levallois-cta-kits"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-levallois-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
